import { useState, useRef, useEffect } from 'react';
import { useActions, useLevels, useStrategy } from '@/store/selectors';
import { LevelType, PKSource } from '@/types';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { Collapsible } from '@/components/shared/Collapsible';
import { NomogramPlot } from '@/components/visualization/NomogramPlot';
import { RenalMonitoring } from '@/components/panels/RenalMonitoring';
import { ToxicityRecheck } from '@/components/panels/ToxicityRecheck';
import { ExpressionInput, Select } from 'calc-controls';
import { levelTypeLabel } from '@/utils/format';

/** Levels screen: Hartford random level entry, Sawchuk-Zaske peak/trough, PK display (Phase 2). */
export function LevelsScreen(): JSX.Element {
  const { levels, pkParameters } = useLevels();
  const actions = useActions();
  const strategy = useStrategy();
  const [levelValue, setLevelValue] = useState('');
  const [levelType, setLevelType] = useState(LevelType.Random);
  const [drawTime, setDrawTime] = useState('');
  const [drawOffset, setDrawOffset] = useState('');
  const [doseNum, setDoseNum] = useState('1');
  const [hartfordValue, setHartfordValue] = useState('');
  const [hartfordOffset, setHartfordOffset] = useState('');
  const [flashId, setFlashId] = useState<string | null>(null);
  const [hartfordPlotted, setHartfordPlotted] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hartfordTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    if (hartfordTimer.current) clearTimeout(hartfordTimer.current);
  }, []);

  const triggerFlash = (id: string): void => {
    setFlashId(id);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashId(null), 1600);
  };

  const addLevel = (): void => {
    const id = crypto.randomUUID();
    actions.addLevel({
      id,
      valueMg: Number(levelValue),
      type: levelType,
      drawnAtISO: drawTime || new Date().toISOString(),
      drawOffsetFromInfusionEndMin: Number(drawOffset) || 0,
      doseNumber: Number(doseNum) || 1,
    });
    setLevelValue('');
    setDrawTime('');
    setDrawOffset('');
    triggerFlash(id);
  };

  return (
    <div className="screen" data-testid="levels-screen">
      <Breadcrumb />
      <h1 className="screen-title">Drug Levels & PK</h1>

      <ErrorBoundary label="Nomogram">
        <NomogramPlot />
      </ErrorBoundary>

      <section className="panel" aria-label="Hartford random level">
        <h2 className="panel-title">Hartford Random Level</h2>
        <div className="field-row">
          <div className="field">
            <ExpressionInput
              id="hf-level"
              label="Level"
              unit="mg/L"
              value={Number(hartfordValue) || 0}
              onChange={(v) => setHartfordValue(String(v))}
              min={0}
              decimals={1}
              inline
            />
          </div>
          <div className="field">
            <ExpressionInput
              id="hf-time"
              label="Draw time"
              unit="hr post-infusion"
              value={Number(hartfordOffset) || 0}
              onChange={(v) => setHartfordOffset(String(v))}
              min={0}
              decimals={1}
              inline
            />
          </div>
        </div>
        <button
          className="btn mt-3"
          disabled={!hartfordValue || !hartfordOffset || hartfordPlotted}
          onClick={() => {
            actions.setHartfordLevel({ valueMg: Number(hartfordValue), drawTimeHr: Number(hartfordOffset) });
            setHartfordPlotted(true);
            hartfordTimer.current = setTimeout(() => setHartfordPlotted(false), 1800);
          }}
          data-testid="hf-set"
        >
          {hartfordPlotted ? 'Plotted ✓' : 'Plot on Nomogram'}
        </button>
      </section>

      <section className="panel" aria-label="Level entry">
        <h2 className="panel-title">Measured Levels</h2>
        <div className="field-row">
          <div className="field">
            <ExpressionInput
              id="lev-value"
              label="Value"
              unit="mg/L"
              value={Number(levelValue) || 0}
              onChange={(v) => setLevelValue(String(v))}
              min={0}
              decimals={1}
              inline
            />
          </div>
          <div className="field">
            <Select
              label="Type"
              options={Object.values(LevelType).map((t) => ({ value: t, label: levelTypeLabel(t) }))}
              value={levelType}
              onChange={(v) => setLevelType(v as LevelType)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <ExpressionInput
              id="lev-offset"
              label="Offset from infusion end"
              unit="min"
              value={Number(drawOffset) || 0}
              onChange={(v) => setDrawOffset(String(v))}
              min={0}
              decimals={0}
              inline
            />
          </div>
          <div className="field">
            <ExpressionInput
              id="lev-dose"
              label="Dose #"
              value={Number(doseNum) || 0}
              onChange={(v) => setDoseNum(String(v))}
              min={1}
              decimals={0}
              inline
            />
          </div>
        </div>
        <button className="btn mt-3" disabled={!levelValue} onClick={addLevel} data-testid="add-level">
          Add Level
        </button>
        <span className="sr-only" role="status" aria-live="polite">
          {flashId ? 'Level added' : ''}
        </span>

        {levels.length === 0 && (
          <div className="empty-state">No drug levels yet. Plot a Hartford random level above to classify the dosing interval, or add peak/trough values for Sawchuk-Zaske analysis.</div>
        )}

        {levels.length > 0 && (
          <div className="table-scroll">
            <table className="data-table mt-3">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Raw (mg/L)</th>
                  <th>Corrected (mg/L)</th>
                  <th>Offset (min)</th>
                  <th>Dose #</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {levels.map((l) => (
                  <tr key={l.id} className={flashId === l.id ? 'flash-success' : undefined}>
                    <td>{l.type}</td>
                    <td>{l.valueMg.toFixed(1)}</td>
                    <td>
                      {l.correctedValueMg != null ? (
                        <span>{l.correctedValueMg.toFixed(1)} <button className="btn btn-secondary btn-sm" onClick={() => actions.applyDrawTimeCorrection(l.id)}>Correct</button></span>
                      ) : (
                        <span className="hint-text">—</span>
                      )}
                    </td>
                    <td>{l.drawOffsetFromInfusionEndMin}</td>
                    <td>{l.doseNumber}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => actions.removeLevel(l.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {pkParameters && (
        <section className="panel panel-result" aria-label="PK parameters">
          <h2 className="panel-title">Patient-Specific PK</h2>
          <div className="value-row">
            <span className="value-label">Method</span>
            <span className="value-num">{pkParameters.method}</span>
          </div>
          <div className="value-row">
            <span className="value-label">k<sub>el</sub></span>
            <span className="value-num">{pkParameters.kelPerHr.toFixed(4)} hr⁻¹</span>
          </div>
          <div className="value-row">
            <span className="value-label">Half-life</span>
            <span className="value-num">{pkParameters.halfLifeHr.toFixed(1)} hr</span>
          </div>
          <div className="value-row">
            <span className="value-label">V<sub>d</sub></span>
            <span className="value-num">{pkParameters.vdL.toFixed(1)} L ({pkParameters.vdLkg.toFixed(2)} L/kg)</span>
          </div>
          <div className="value-row">
            <span className="value-label">Confidence</span>
            <span className="value-num">{pkParameters.confidence}</span>
          </div>
        </section>
      )}

      <Collapsible title="Sawchuk-Zaske computation" defaultOpen={false}>
        <p className="hint-text">Requires at least one peak and one trough level from the same dose.</p>
        <button className="btn" onClick={() => actions.runSawchukZaske()} disabled={levels.length < 2}>
          Run Sawchuk-Zaske
        </button>
      </Collapsible>

      <Collapsible title="PK source toggle" defaultOpen={false}>
        <p className="hint-text">
          Switch between population PK (default) and patient-specific PK (requires Sawchuk-Zaske or Bayesian results).
        </p>
        <div className="field-row">
          <button
            className={`btn btn-sm${strategy.pkSource === PKSource.Population ? '' : ' btn-secondary'}`}
            onClick={() => actions.setPKSource(PKSource.Population)}
            disabled={strategy.pkSource === PKSource.Population}
            data-testid="pk-source-population"
          >
            Population PK
          </button>
          <button
            className={`btn btn-sm${strategy.pkSource === PKSource.PatientSpecific ? '' : ' btn-secondary'}`}
            onClick={() => actions.setPKSource(PKSource.PatientSpecific)}
            disabled={!pkParameters || strategy.pkSource === PKSource.PatientSpecific}
            data-testid="pk-source-patient"
          >
            Patient-Specific PK {!pkParameters && '(requires levels)'}
          </button>
        </div>
        {strategy.pkSource === PKSource.PatientSpecific && pkParameters && (
          <div className="hint-text">
            Using patient-specific PK: kₑl = {pkParameters.kelPerHr.toFixed(4)} hr⁻¹, Vd = {pkParameters.vdLkg.toFixed(2)} L/kg.
            Confidence upgraded to {pkParameters.confidence}.
          </div>
        )}
      </Collapsible>

      <ErrorBoundary label="Renal monitoring">
        <RenalMonitoring />
      </ErrorBoundary>

      <ErrorBoundary label="Toxicity recheck">
        <ToxicityRecheck />
      </ErrorBoundary>
    </div>
  );
}
